import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { memberApi } from '../../config/api.config';
import useUserStore from '../../store/useUserStore';
import Loading from '../common/Loading';
import Pagination from '../common/Pagination';
import LimitSelect from '../common/LimitSelect';
import MemberModal from './MemberModal';
import EditMemberModal from './EditMemberModal';

const MemberList = () => {
    const nav = useNavigate();
    const { userToken, isTokenValid } = useUserStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);

    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 10;

    useEffect(() => {
        if (!userToken || !isTokenValid()) {
            alert('관리자 권한이 없습니다.');
            nav('/login');
            return;
        }
    }, [userToken, isTokenValid, nav]);

    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    useEffect(() => {
        setLoading(true);
        const searchQuery = searchTerm ? `&search=${searchTerm}` : '';
        const url = `/admin/members?page=${page}&limit=${limit}${searchQuery}`;
        
        console.log('검색 실행:', { searchTerm, searchQuery, url });
        
        memberApi.get(url)
            .then(res => {
                console.log('검색 결과:', res.data);
                setMembers(res.data.members || res.data || []);
                setTotal(res.data.total || 0);
                setLoading(false);
            }).catch(err => {
                console.log('검색 에러:', err);
                setLoading(false);
            })
    }, [page, limit, searchTerm]);

    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage, limit });
    };

    const handleLimitChange = (newLimit) => {
        setSearchParams({ page: 1, limit: newLimit });
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        
        console.log('검색 버튼 클릭:', searchInput);
        
        // 기존 타이머 취소
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        // 즉시 검색 실행
        setSearchTerm(searchInput);
        setSearchParams({ page: 1, limit });
    };

    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        
        // 기존 타이머 취소
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        // 300ms 후에 검색 실행 (디바운싱)
        const timeout = setTimeout(() => {
            console.log('디바운싱 검색 실행:', value);
            setSearchTerm(value);
            setSearchParams({ page: 1, limit });
        }, 300);
        
        setSearchTimeout(timeout);
    };

    const handleSearchClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setSearchParams({ page: 1, limit });
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
    };

    const handleDelete = (id) => {
        if(window.confirm('정말로 삭제하시겠습니까?')) {
            memberApi.delete(`/admin/members/${id}`)
                .then(() => {
                    // 삭제 후 현재 페이지의 데이터를 다시 불러옴
                    const searchQuery = searchTerm ? `&search=${searchTerm}` : '';
                    return memberApi.get(`/admin/members?page=${page}&limit=${limit}${searchQuery}`);
                })
                .then(res => {
                    setMembers(res.data.members || res.data || []);
                    setTotal(res.data.total || 0);
                    
                    // 현재 페이지가 1페이지가 아니고, 현재 페이지의 마지막 항목을 삭제한 경우 1페이지로 이동
                    const currentPageItems = res.data.members?.length || 0;
                    if (page > 1 && currentPageItems === 0) {
                        setSearchParams({ page: page - 1, limit });
                    }
                })
                .catch(err => {
                    console.log(err);
                    alert('삭제에 실패했습니다.');
                });
        }
    };

    const getProviderName = (provider) => {
        switch(provider) {
            case 'kakao': return '카카오';
            case 'apple': return '애플';
            case 'google': return '구글';
            case 'custom': return '일반';
            default: return provider;
        }
    };

    const totalPages = Math.ceil(total / limit);
    const groupSize = 5;

    if (!userToken || !isTokenValid()) {
        return null;
    }

    if (loading) {
        return <Loading text="회원 목록을 불러오는 중..." />;
    }

  return (
        <div style={{ maxWidth: 1000, margin: '40px auto 0 auto', padding: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, borderBottom: '2px solid #1976d2', paddingBottom: 8, color: '#222', margin: 0 }}>
                    회원 전체 목록
                </h2>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1976d2', paddingBottom: 8 }}>
                    총 {total}명
                </div>
            </div>
            
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="이메일, 이름, 전화번호로 검색"
                            value={searchInput}
                            onChange={handleSearchInputChange}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                            style={{
                                padding: '8px 12px 8px 35px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                width: '250px'
                            }}
                        />
                        <span style={{
                            position: 'absolute',
                            left: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999',
                            fontSize: '14px'
                        }}>
                            🔍
                        </span>
                        {searchInput && (
                            <button
                                type="button"
                                onClick={handleSearchClear}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: '#999',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    padding: '0',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseOver={e => {
                                    e.currentTarget.style.color = '#d32f2f';
                                }}
                                onMouseOut={e => {
                                    e.currentTarget.style.color = '#999';
                                }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleSearch}
                        style={{
                            padding: '8px 16px',
                            background: '#1976d2',
                            color: '#fff',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'background 0.2s, color 0.2s'
                        }}
                        onMouseOver={e => {
                            e.currentTarget.style.background = '#fff';
                            e.currentTarget.style.color = '#1976d2';
                        }}
                        onMouseOut={e => {
                            e.currentTarget.style.background = '#1976d2';
                            e.currentTarget.style.color = '#fff';
                        }}
                    >
                        검색
                    </button>
                    {searchTerm && (
                        <span style={{ fontSize: '12px', color: '#666' }}>
                            검색어: "{searchTerm}"
                        </span>
                    )}
                </div>
                <LimitSelect limit={limit} onLimitChange={handleLimitChange} />
            </div>
            
            <div style={{ minHeight: 220 }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</div>
                ) : (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                                <tr>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '5%' }}>No.</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'left', fontWeight: 700, color: '#222', width: '18%' }}>사용자명</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '27%' }}>이메일</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '18%' }}>전화번호</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '12%' }}>포인트</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '10%' }}>가입경로</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '10%' }}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.length > 0 ? (
                                    members.map((member, index) => {
                                        const rowNumber = (page - 1) * limit + index + 1;
                                        return (
                                        <tr key={member._id} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#888', textAlign: 'center', fontWeight: '500' }}>
                                                {rowNumber}
                                            </td>
                                            <td
                                                style={{
                                                    padding: '14px 10px',
                                                    fontSize: 17,
                                                    color: '#222',
                                                    transition: 'font-weight 0.2s'
                                                }}
                                                onMouseOver={e => {
                                                    e.currentTarget.style.fontWeight = '700';
                                                }}
                                                onMouseOut={e => {
                                                    e.currentTarget.style.fontWeight = '500';
                                                }}
                                            >
                                                {member.memberNickname || '-'}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {member.memberEmail || '-'}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {member.memberPhone || '-'}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {member.memberPoints || 0}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {getProviderName(member.provider)}
                                            </td>
                                            <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                                    <button
                                                        style={{
                                                            padding: '7px 16px',
                                                            background: '#1976d2',
                                                            color: '#fff',
                                                            border: '1px solid #1976d2',
                                                            borderRadius: 4,
                                                            cursor: 'pointer',
                                                            fontSize: 15,
                                                            transition: 'background 0.2s, color 0.2s',
                                                            whiteSpace: 'nowrap',
                                                            textAlign: 'center',
                                                        }}
                                                        onMouseOver={e => {
                                                            e.currentTarget.style.background = '#fff';
                                                            e.currentTarget.style.color = '#1976d2';
                                                            e.currentTarget.style.border = '1px solid #1976d2';
                                                        }}
                                                        onMouseOut={e => {
                                                            e.currentTarget.style.background = '#1976d2';
                                                            e.currentTarget.style.color = '#fff';
                                                            e.currentTarget.style.border = '1px solid #1976d2';
                                                        }}
                                                        onClick={() => nav(`/admin/members/${member._id}/vehicles`)}
                                                    >관리</button>
                                                    <button
                                                        style={{
                                                            padding: '7px 16px',
                                                            background: '#d32f2f',
                                                            color: '#fff',
                                                            border: '1px solid #d32f2f',
                                                            borderRadius: 4,
                                                            cursor: 'pointer',
                                                            fontSize: 15,
                                                            transition: 'background 0.2s, color 0.2s',
                                                            whiteSpace: 'nowrap',
                                                            textAlign: 'center',
                                                        }}
                                                        onMouseOver={e => {
                                                            e.currentTarget.style.background = '#fff';
                                                            e.currentTarget.style.color = '#d32f2f';
                                                            e.currentTarget.style.border = '1px solid #d32f2f';
                                                        }}
                                                        onMouseOut={e => {
                                                            e.currentTarget.style.background = '#d32f2f';
                                                            e.currentTarget.style.color = '#fff';
                                                            e.currentTarget.style.border = '1px solid #d32f2f';
                                                        }}
                                                        onClick={() => handleDelete(member._id)}
                                                    >삭제</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ 
                                        padding: '40px 20px', 
                                        textAlign: 'center', 
                                        color: '#666',
                                        fontSize: '14px'
                                    }}>
                                        등록된 회원이 없습니다.
                                    </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <Pagination page={page} totalPages={totalPages} groupSize={groupSize} onChange={handlePageChange} />
                        <div style={{ textAlign: 'right', marginTop: 20 }}>
                            <button
                                style={{
                                    background: '#1976d2',
                                    color: '#fff',
                                    border: '1px solid #1976d2',
                                    borderRadius: 4,
                                    padding: '8px 20px',
                                    fontSize: 15,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'background 0.2s, color 0.2s',
                                }}
                                onMouseOver={e => {
                                    e.currentTarget.style.background = '#fff';
                                    e.currentTarget.style.color = '#1976d2';
                                    e.currentTarget.style.border = '1px solid #1976d2';
                                }}
                                onMouseOut={e => {
                                    e.currentTarget.style.background = '#1976d2';
                                    e.currentTarget.style.color = '#fff';
                                    e.currentTarget.style.border = '1px solid #1976d2';
                                }}
                                onClick={() => setIsModalOpen(true)}
                            >
                                생성
                            </button>
                        </div>
                    </>
                )}
            </div>
            
            <MemberModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={() => {
                    // 회원 생성 후 목록 새로고침
                    memberApi.get(`/admin/members?page=${page}&limit=${limit}`)
                        .then(res => {
                            setMembers(res.data.members || []);
                            setTotal(res.data.total || 0);
                        })
                        .catch(err => {
                            console.log(err);
                        });
                }}
            />
            
            <EditMemberModal 
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedMemberId(null);
                }}
                onSuccess={() => {
                    // 회원 수정 후 목록 새로고침
                    memberApi.get(`/admin/members?page=${page}&limit=${limit}`)
                        .then(res => {
                            setMembers(res.data.members || []);
                            setTotal(res.data.total || 0);
                        })
                        .catch(err => {
                            console.log(err);
                        });
                }}
                memberId={selectedMemberId}
            />
        </div>
  )
}

export default MemberList